import { useLocalStorage } from 'hooks/shared';
import { useEffect } from 'react';
import { useIsFeatureToggleEnabled } from '@/features/feature-toggle/lib';
import { useCurrentUserQuery } from 'shared/legacy/hooks/useCurrentUserQuery';
import { fallbackSaveReceivedMessages } from '../fallbackSaveReceivedMessages';
import { MessageReceivedPlatform } from '../platform.types';
import { TransformedIndeedMessage } from '../types';
import { useGetNewIndeedMessages } from './indeed.hooks';
import { IndeedConversation } from './indeed.types';
import { useIndeedConfig } from './useIndeedConfig';
import { saveContactInfo, saveIndeedMessagesNotification } from './useUnreadIndeedMessages.utils';

const PROCESSED_INDEED_MESSAGES_NOTIFICATION = 'processedIndeedMessagesNotification';

export const useUnreadIndeedMessages = () => {
  const isExtensionPollingEnabled = useIsFeatureToggleEnabled((t) => t.INDEED_POLLING_ENABLED);
  const indeedConfig = useIndeedConfig();
  const { newIndeedMessages } = useGetNewIndeedMessages();
  const { value: getProcessedMessages, setValue: setProcessedMessages } = useLocalStorage<string[]>(
    PROCESSED_INDEED_MESSAGES_NOTIFICATION,
    [],
  );

  const { data: currentUser } = useCurrentUserQuery();

  useEffect(() => {
    if (isExtensionPollingEnabled) return;

    const messagesToProcess = newIndeedMessages.filter((m) => !getProcessedMessages.includes(m.lastEvent.id));

    const runOrchestratorNewMessage = async (messagesToProcess: IndeedConversation[]) => {
      const candidatesToProcess = await saveIndeedMessagesNotification({ messagesToProcess });

      if (candidatesToProcess?.candidates.length === 0) {
        return;
      }

      for (const candidate of candidatesToProcess?.candidates || []) {
        await saveContactInfo({ candidate, indeedConfig });

        const findMessagesBasedOnCandidateName = messagesToProcess.filter((message) =>
          message.participants.some((participant) => participant.participantName === candidate.candidate_name),
        );

        if (findMessagesBasedOnCandidateName.length === 0) return;

        const transformedMessages: TransformedIndeedMessage[] = findMessagesBasedOnCandidateName.map((conversation) => {
          const senderId = conversation.lastEvent.author.accountKey;

          const authorParticipant = conversation.participants.find(
            (participant) => participant.accountKey === senderId,
          );
          const fromSelf = authorParticipant?.role === 'EMPLOYER';

          return {
            senderId,
            content: conversation.lastEvent.messageBody,
            fromSelf,
            sentAt: new Date(conversation.lastEvent.publicationDateTime).getTime(),
            id: conversation.lastEvent.id,
          };
        });

        await fallbackSaveReceivedMessages({
          currentUser,
          messages: transformedMessages,
          candidate,
          platform: MessageReceivedPlatform.INDEED,
        });
      }
    };

    if (messagesToProcess.length > 0) {
      runOrchestratorNewMessage(messagesToProcess).finally(() => {
        setProcessedMessages(newIndeedMessages.map((i) => i.lastEvent.id));
      });
    }
  }, [newIndeedMessages]);
};
